import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatTab, MatTabChangeEvent, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { NgClass } from '@angular/common';
import { DetailsComponent } from './components/details/details.component';
import { ContainersComponent } from './components/containers/containers.component';
import { RoutingComponent } from './components/routing/routing.component';
import { ShipmentDetailsService } from './services/shipment-details.service';
import { catchError, NEVER, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { ScanButtonsComponent } from './components/details/scan-buttons/scan-buttons.component';
import { DocumentsComponent } from './components/documents/documents.component';
import { DocumentListComponent } from './components/documents/document-list/document-list.component';
import { LastEventComponent } from './components/last-event/last-event.component';
import { AllEventsComponent } from './components/all-events/all-events.component';
import { CustomFieldsComponent } from './components/custom-fields/custom-fields.component';
import { Router } from '@angular/router';
import { Shipment } from '../../pages/overview/models/shipment';
import { Clipboard } from '@angular/cdk/clipboard';
import { environment } from '../../../environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GoogleTagManagerService } from '../../services/google-tag-manager.service';
import { PurchaseOrderListComponent } from '../purchase-order-list/purchase-order-list.component';

@Component({
  selector: 'app-shipment-details',
  standalone: true,
  templateUrl: './shipment-details.component.html',
  imports: [
    MatTabGroup,
    MatTab,
    MatTabLabel,
    DetailsComponent,
    ContainersComponent,
    RoutingComponent,
    NgClass,
    MatTooltip,
    ScanButtonsComponent,
    DocumentsComponent,
    DocumentListComponent,
    LastEventComponent,
    AllEventsComponent,
    CustomFieldsComponent,
    PurchaseOrderListComponent
  ],
  styleUrl: './shipment-details.component.scss'
})
export class ShipmentDetailsComponent implements OnChanges {
  @Input() set shipment(value: Shipment | null) {
    if (value) {
      this.shipmentData = value;
      this.isBeingWatched = this.shipmentData.watched;
    }
  }

  @Input() shipmentNo: string | null = null;
  @Input() openedFrom: 'Overview' | 'Shipments' = 'Overview';
  @Input() isArchived = false;
  @Output() detailsClosed = new EventEmitter();

  shipmentData: Shipment | null = null;
  isBeingWatched = false;

  private shipmentDetailsService = inject(ShipmentDetailsService);
  private destroyRef = inject(DestroyRef);
  private matSnackBar = inject(MatSnackBar);
  private router = inject(Router);
  private copyToClipboard = inject(Clipboard);
  private gtmService = inject(GoogleTagManagerService);

  ngOnChanges(changes: SimpleChanges) {
    if (changes['shipmentNo'] && changes['shipmentNo'].currentValue) {
      this.shipmentData = null;
      this.shipmentDetailsService
        .getShipmentBy(this.shipmentNo!, this.isArchived)
        .pipe(
          tap(shipment => {
            this.shipmentData = shipment;
            this.isBeingWatched = shipment.watched;
          }),
          catchError(error => {
            this.matSnackBar.open('Could not get the shipment details. Please try again.', 'x');
            console.error(error);
            return NEVER;
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  toggleWatch() {
    this.shipmentDetailsService
      .watched(this.shipmentData!.shipmentNo, !this.isBeingWatched)
      .pipe(
        tap(() => {
          if (this.isBeingWatched) {
            this.handleSuccess('Shipment removed from watch list');
          } else {
            this.handleSuccess('Shipment added to watch list');
          }
          this.isBeingWatched = !this.isBeingWatched;
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError(error => this.handleError(error))
      )
      .subscribe();
  }

  onClose() {
    if (this.openedFrom === 'Overview') {
      this.router.navigate(['/']);
    } else {
      this.detailsClosed.emit();
    }
  }

  copyTrackingLink() {
    if (this.shipmentData) {
      this.copyToClipboard.copy(
        `${environment.publicTrackingUrl}/${this.shipmentData.shipmentNo}/details/${this.shipmentData.shipmentNo}`
      );
      this.matSnackBar.open('Public tracking link was copied to clipboard', 'x', { panelClass: 'snackbar-green' });
    }
  }

  onTabChange({ tab }: MatTabChangeEvent) {
    this.gtmService.pushEvent({
      event: 'shipment_details',
      type: {
        tab_viewed: tab.textLabel
      }
    });
  }

  private handleSuccess(message: string) {
    this.matSnackBar.open(message, 'x', { panelClass: 'snackbar-green' });
  }

  private handleError(error: HttpErrorResponse) {
    this.matSnackBar.open('Could not update shipment watch status, please try again later.', 'x');
    console.error(error);

    return NEVER;
  }
}
